import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { Metric } from 'src/constants/types';
import { Granularity, MetricChartData, MetricChartLine } from '../types';

dayjs.extend(isoWeek);

export const formatMetricDate = (value: string) => {
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('MM/DD') : value;
};

export const formatMetricValue = (value: number) => value.toLocaleString();

export const CHART_TICK_FONT_SIZE = 12;
export const Y_AXIS_GUTTER = 12;
export const Y_AXIS_MIN_WIDTH = 48;

// 0.56em is the digit advance in the MUI default font stack.
export const getTickCharWidth = (fontSize: number): number => Math.ceil(fontSize * 0.56);

export const getYAxisWidth = (
  data: MetricChartData[],
  lines: MetricChartLine[],
  { stacked = false }: { stacked?: boolean } = {},
): number => {
  const maxValue = data.reduce((highest, entry) => {
    const values = lines.map(line => toMetricNumber(entry[line.dataKey as keyof MetricChartData]));
    const bucket = stacked ? values.reduce((sum, value) => sum + value, 0) : Math.max(0, ...values);
    return Math.max(highest, bucket);
  }, 0);

  const charCount = formatMetricValue(maxValue).length + 1;
  const charWidth = getTickCharWidth(CHART_TICK_FONT_SIZE);
  return Math.max(Y_AXIS_MIN_WIDTH, charCount * charWidth + Y_AXIS_GUTTER);
};

export const toMetricNumber = (value: unknown) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

export const getMetricChartData = (metrics: Metric[]): MetricChartData[] =>
  metrics
    .map(metric => {
      const sortDate = metric.startDateInterval || metric.endDateInterval;

      return {
        label: formatMetricDate(sortDate),
        sortDate,
        bucketStart: metric.startDateInterval || metric.endDateInterval,
        bucketEnd: metric.endDateInterval || metric.startDateInterval,
        inboundCredits: toMetricNumber(metric.inboundCredits),
        outboundCredits: toMetricNumber(metric.outboundCredits),
        contactValidationCredits: Math.abs(toMetricNumber(metric.lookupsCredits)),
        contactValidationCount: toMetricNumber(metric.lookupsCount),
        smsCount: toMetricNumber(metric.smsCount),
        mmsCount: toMetricNumber(metric.mmsCount),
        campaignMessagesCount: toMetricNumber(metric.campaignMessagesCount),
        inboxMessagesCount: toMetricNumber(metric.inboxMessagesCount),
        newContactsCount: toMetricNumber(metric.newContactsCount),
      };
    })
    .sort((a, b) => dayjs(a.sortDate).valueOf() - dayjs(b.sortDate).valueOf());

// X-axis tick for one BE bucket. Weekly shows the full start–end span so the
// user can read the period at a glance; coarser buckets read fine as a single
// point. Uses the bucket's actual start/end from the BE, not a derived date.
export const formatBucketLabel = (
  bucketStart: string,
  bucketEnd: string,
  granularity: Granularity,
): string => {
  const start = dayjs(bucketStart);
  const end = dayjs(bucketEnd);
  switch (granularity) {
    case 'week':
      return `${start.format('MM/DD')}–${end.format('MM/DD')}`;
    case 'month':
      return start.format('MMM');
    case 'year':
      return start.format('YYYY');
    case 'day':
    default:
      return start.format('MM/DD');
  }
};

export const getMetricTotals = (data: MetricChartData[]): Record<string, number> =>
  data.reduce(
    (totals, metric) => ({
      inboundCredits: totals.inboundCredits + metric.inboundCredits,
      outboundCredits: totals.outboundCredits + metric.outboundCredits,
      contactValidationCredits: totals.contactValidationCredits + metric.contactValidationCredits,
      contactValidationCount: totals.contactValidationCount + metric.contactValidationCount,
      smsCount: totals.smsCount + metric.smsCount,
      mmsCount: totals.mmsCount + metric.mmsCount,
      campaignMessagesCount: totals.campaignMessagesCount + metric.campaignMessagesCount,
      inboxMessagesCount: totals.inboxMessagesCount + metric.inboxMessagesCount,
      newContactsCount: totals.newContactsCount + metric.newContactsCount,
    }),
    {
      inboundCredits: 0,
      outboundCredits: 0,
      contactValidationCredits: 0,
      contactValidationCount: 0,
      smsCount: 0,
      mmsCount: 0,
      campaignMessagesCount: 0,
      inboxMessagesCount: 0,
      newContactsCount: 0,
    },
  );

// Empty data reduces to all-zero totals, so this also covers "no rows at all".
export const areMetricTotalsEmpty = (
  totals: Record<string, number>,
  lines: MetricChartLine[],
): boolean => lines.every(line => !totals[line.dataKey]);
